/*
PW List Builder

A component that allows building up a list of values. Existing values in the list
can be modified and deleted, and new values can be added to the end of the list.

Items will be added/edited using a ComboBox component; if an `options` prop is provided
then its values will be used for the ComboBox's dropdown.

If an `isValidValue` function is provided in the props, then it will be called for each
existing item in the list; any items for which this function returns `false` will be
marked with an "invalid" class which can be used for error styling.

TODO:
  - Allow reordering items
  - Flag for automatically opening another New Item row after adding a new item
  - Allow specific validation error messages rather than just a simple valid/invalid boolean?

*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import ComboBox from './ComboBox'

class ListBuilder extends React.Component {
  static displayName = 'ListBuilder'

  static propTypes = {
    initialValues: T.array,
    onValuesChanged: T.func.isRequired,
    addPlaceholder: T.string,
    isValidValue: T.func,
    options: T.array,
    requireMatch: T.bool,
    className: T.string,
    inline: T.bool,
    readOnly: T.bool,
    shrinkWrapInputs: T.bool,
    allowDuplicates: T.bool
  }

  static defaultProps = {
    addPlaceholder: '',
    className: '',
    inline: false
  }

  state = {
    isAdding: false,
    values: []
  }

  UNSAFE_componentWillMount() {
    const initialValues = this.props.initialValues
    this.setState({
      values: initialValues ? initialValues.slice() : []
    })
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const initialValues = newProps.initialValues
    if (!_.isEqual(initialValues, this.props.initialValues)) {
      this.setState({
        values: initialValues ? initialValues.slice() : []
      })
    }
  }

  componentDidMount() {
    this._shrinkWrapInputs()
  }

  componentDidUpdate() {
    this._shrinkWrapInputs()
  }

  _shrinkWrapInputs = () => {
    if (this.props.shrinkWrapInputs) {
      const inputs = ReactDOM.findDOMNode(this).querySelectorAll('.grid_input')
      for (let i = 0, len = inputs.length; i < len; i++) {
        inputs[i].style.width = inputs[i].scrollWidth + 'px'
      }
    }
  }

  _onDeleteClicked = (i, e) => {
    e.preventDefault()
    const values = this.state.values.slice()
    values.splice(i, 1)
    this._notifyChange(values)
  }

  _onItemChanged = (i, val) => {
    let values = this.state.values.slice()
    values[i] = val
    if (!this.props.allowDuplicates) {
      values = _.uniq(values)
    }
    this._notifyChange(values)
  }

  _onAddClicked = (e) => {
    e.preventDefault()
    this.setState({ isAdding: true }, () => {
      const field = this._comboboxRef
      if (field) field.focus()
    })
  }

  _onNewItemSaved = (value) => {
    let values = this.state.values.slice()
    values.push(value)
    if (!this.props.allowDuplicates) {
      values = _.uniq(values)
    }
    this._notifyChange(values)
    this.setState({ isAdding: false })
  }

  _onNewItemCanceled = () => {
    this.setState({ isAdding: false })
  }

  _notifyChange = (values) => {
    const fn = this.props.onValuesChanged
    if (fn) {
      fn(values)
    }
  }

  _bindComboboxRef = comp => {
    this._comboboxRef = comp
  }

  render() {
    const { props, state } = this
    const values = state.values || []
    const isAdding = state.isAdding
    let options = props.options || []
    const readOnly = props.readOnly
    const classes = cx(props.className || '', {
      pw_list_builder: true,
      isEmpty: !values.length,
      isAdding: isAdding,
      inline: props.inline,
      readonly: readOnly
    })

    // Remove selected values from the options list. They still need to be in the options list
    // for ComboBox to look up their display labels, so we just hide them.
    if (!props.allowDuplicates) {
      options = options.map(opt => {
        const optValue = _.isObject(opt) ? opt.value : opt
        if (_.includes(values, optValue)) {
          opt = _.isObject(opt)
            ? _.assign({}, opt)
            : { value: opt, display: opt }
          opt.className = cx(opt.className, 'hidden')
        }
        return opt
      })
    }

    return (
      <div className={classes}>
        <ul>
          {values.map((val, i) => {
            const liClasses = cx({
              invalid: props.isValidValue ? !props.isValidValue(val) : false
            })
            return (
              <li key={i} className={liClasses}>
                <ComboBox
                  className="grid_input stealth"
                  initialValue={val}
                  onSave={_.partial(this._onItemChanged, i)}
                  saveOnBlur
                  blurOnSave
                  cancelOnEsc
                  options={options}
                  requireMatch={props.requireMatch}
                  readOnly={readOnly}
                  scrollable
                  size={
                    props.inline ? 1 : null //prevent min width from default size
                  }
                />
                {!readOnly &&
                  <a
                    href="#"
                    className="icon icon-close"
                    onClick={_.partial(this._onDeleteClicked, i)}
                  />}
              </li>
            )
          })}
          {!readOnly &&
            <li key="addRow" className="addRow">
              {isAdding
                ? <ComboBox
                    ref={this._bindComboboxRef}
                    className="grid_input stealth"
                    onSave={this._onNewItemSaved}
                    onCancel={this._onNewItemCanceled}
                    saveOnBlur
                    clearOnSave
                    cancelOnEsc
                    placeholder={props.addPlaceholder}
                    options={options}
                    requireMatch={props.requireMatch}
                    scrollable
                  />
                : <a
                    href="#"
                    className="icon icon-plus"
                    onClick={this._onAddClicked}
                  />}
            </li>}
        </ul>
      </div>
    )
  }
}

export default ListBuilder
